import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTabActive } from "@/layouts/adminTabs/tabRuntime";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { getApiErrorMessage } from "@/lib/api";
import {
  concludeEvaluation,
  downloadFichaCsv,
  fetchFicha,
  saveEvaluation,
  type Compatibility,
  type EvaluatorFicha,
  type EvaluatorQuestion,
  type SelectionDecision,
  type StageResult,
  type TiebreakConfidence,
} from "@/lib/adminSelectionApi";

const DECISIONS: { v: SelectionDecision; l: string }[] = [
  { v: "NAO_APROVAR", l: "Não aprovar" },
  { v: "MANTER", l: "Manter no processo" },
  { v: "APROVAR", l: "Aprovar" },
  { v: "PRIORIDADE", l: "Prioridade de contratação" },
];
const COMPAT: { v: Compatibility; l: string }[] = [
  { v: "NAO", l: "Não" }, { v: "POUCO", l: "Pouco" }, { v: "COMPATIVEL", l: "Compatível" },
  { v: "MUITO", l: "Muito" }, { v: "EXCELENTE", l: "Excelente" },
];
const TIEBREAK: { v: TiebreakConfidence; l: string }[] = [
  { v: "SIM_SEM_HESITACAO", l: "SIM, sem hesitação" },
  { v: "SIM_COM_ACOMPANHAMENTO", l: "SIM, mas com acompanhamento" },
  { v: "TENHO_DUVIDAS", l: "TENHO DÚVIDAS" },
  { v: "NAO", l: "NÃO" },
];
const STAGE_RESULTS: { v: StageResult; l: string }[] = [
  { v: "AVANCAR", l: "Avançar" }, { v: "AVALIAR", l: "Avaliar" }, { v: "NAO_AVANCAR", l: "Não avançar" },
];

type Form = {
  stageResults: Record<string, { result: string; note: string }>;
  evaluatorAnswers: Record<string, { text: string; score: string }>;
  criterionScores: Record<string, string>;
  practicalScore: string;
  potential: string;
  compatibility: string;
  decision: string;
  tiebreakAnswer: string;
  alerts: string[];
  strengths: string;
  devPoints: string;
  generalOpinion: string;
};

function buildForm(f: EvaluatorFicha): Form {
  const stageResults: Form["stageResults"] = {};
  const evaluatorAnswers: Form["evaluatorAnswers"] = {};
  for (const s of f.stages) {
    stageResults[s.id] = { result: s.result ?? "", note: s.note ?? "" };
    for (const q of s.questions) {
      if (q.visibility === "EVALUATOR") {
        evaluatorAnswers[q.id] = { text: q.evaluatorValueText ?? "", score: q.evaluatorScore?.toString() ?? "" };
      }
    }
  }
  const criterionScores: Form["criterionScores"] = {};
  for (const c of f.criteria) criterionScores[c.criterionId] = c.score?.toString() ?? "";
  let alerts: string[] = [];
  try { const a = JSON.parse(f.alerts ?? "[]"); if (Array.isArray(a)) alerts = a.map(String); } catch { /* ignore */ }
  return {
    stageResults, evaluatorAnswers, criterionScores,
    practicalScore: f.practicalScore?.toString() ?? "",
    potential: f.potential?.toString() ?? "",
    compatibility: f.compatibility ?? "",
    decision: f.decision ?? "",
    tiebreakAnswer: f.tiebreakAnswer ?? "",
    alerts,
    strengths: f.strengths ?? "",
    devPoints: f.devPoints ?? "",
    generalOpinion: f.generalOpinion ?? "",
  };
}

const intOrNull = (s: string): number | null => {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

function alertOptions(f: EvaluatorFicha): string[] {
  const stage = f.stages.find((s) => s.stageKey === "avaliacao_final");
  if (!stage?.meta) return [];
  try { const m = JSON.parse(stage.meta); return Array.isArray(m.alertas) ? m.alertas.map(String) : []; }
  catch { return []; }
}

function candidateAnswerText(q: EvaluatorQuestion): string {
  if (q.candidateValueJson) {
    try { const a = JSON.parse(q.candidateValueJson); if (Array.isArray(a)) return a.join(", "); } catch { /* ignore */ }
  }
  return q.candidateValueText ?? "—";
}

export default function AdminSelectionEvaluation() {
  const { enrollmentId = "" } = useParams();
  const navigate = useNavigate();
  const account = useApp((s) => s.account);
  const canEvaluate = hasPermission(account, "SELECAO.CANDIDATO.AVALIAR");
  const canConclude = hasPermission(account, "SELECAO.CANDIDATO.CONCLUIR");
  const canExport = hasPermission(account, "SELECAO.RANKING.EXPORTAR");
  const [ficha, setFicha] = useState<EvaluatorFicha | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);
  const loadedRef = useRef(false);

  const load = useCallback(async (initForm: boolean) => {
    const f = await fetchFicha(enrollmentId);
    setFicha(f);
    if (initForm) setForm(buildForm(f));
  }, [enrollmentId]);

  useEffect(() => {
    loadedRef.current = false;
    void load(true).then(() => { loadedRef.current = true; }).catch((err) =>
      toast.error(getApiErrorMessage(err, "Falha ao carregar ficha")));
  }, [load]);

  // Pausa o polling quando a aba interna está inativa (keep-alive); retoma ao reativar, sem duplicar timer.
  const tabActive = useTabActive();

  // Poll candidate answers without clobbering the evaluator's in-progress edits (form untouched).
  useEffect(() => {
    if (!tabActive) return;
    const id = setInterval(() => { if (loadedRef.current) void load(false).catch(() => {}); }, 15000);
    return () => clearInterval(id);
  }, [load, tabActive]);

  const alertOpts = useMemo(() => (ficha ? alertOptions(ficha) : []), [ficha]);

  if (!ficha || !form) {
    return <div className="container mx-auto max-w-4xl px-4 py-6 text-sm text-muted-foreground">Carregando…</div>;
  }

  const concluded = ficha.evaluationStatus === "CONCLUIDA";
  const patch = (p: Partial<Form>) => setForm((prev) => (prev ? { ...prev, ...p } : prev));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        stageResults: Object.entries(form.stageResults).map(([stageId, v]) => ({
          stageId, result: (v.result || null) as StageResult | null, note: v.note || null,
        })),
        criterionScores: Object.entries(form.criterionScores).map(([criterionId, s]) => ({
          criterionId, score: intOrNull(s),
        })),
        evaluatorAnswers: Object.entries(form.evaluatorAnswers).map(([questionId, v]) => ({
          questionId, valueText: v.text || null, valueJson: null, score: intOrNull(v.score),
        })),
        parecer: {
          practicalScore: intOrNull(form.practicalScore),
          potential: intOrNull(form.potential),
          compatibility: (form.compatibility || null) as Compatibility | null,
          decision: (form.decision || null) as SelectionDecision | null,
          tiebreakAnswer: (form.tiebreakAnswer || null) as TiebreakConfidence | null,
          alerts: form.alerts.length ? JSON.stringify(form.alerts) : null,
          strengths: form.strengths || null,
          devPoints: form.devPoints || null,
          generalOpinion: form.generalOpinion || null,
        },
      };
      const updated = await saveEvaluation(enrollmentId, body);
      setFicha(updated);
      setForm(buildForm(updated));
      toast.success("Avaliação salva");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Falha ao salvar avaliação"));
    } finally {
      setSaving(false);
    }
  };

  const conclude = async () => {
    if (!window.confirm("Concluir a avaliação? O candidato entrará no ranking definitivo.")) return;
    try {
      const updated = await concludeEvaluation(enrollmentId);
      setFicha(updated);
      setForm(buildForm(updated));
      toast.success("Avaliação concluída");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Falha ao concluir"));
    }
  };

  const scoreStr = ficha.score.finalScore == null ? "—" : ficha.score.finalScore.toFixed(2).replace(".", ",");
  const weightStr = ficha.score.evaluatedWeightPct == null ? "" : `${ficha.score.evaluatedWeightPct.toFixed(0)}%`;

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6">
      <button className="mb-3 text-sm text-muted-foreground underline" onClick={() => navigate(-1)}>← Voltar</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{ficha.candidateName || "Candidato"}</h1>
          <div className="text-xs text-muted-foreground">
            Código <span className="font-mono">{ficha.accessCode}</span> · {ficha.enrollmentStatus}
            {concluded && ficha.concludedByName && <> · concluída por {ficha.concludedByName}</>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">Nota {scoreStr}{ficha.score.partial ? ` (parcial ${weightStr})` : ""}</Badge>
          {canExport && <Button size="sm" variant="ghost" onClick={() => void downloadFichaCsv(enrollmentId)}>Exportar CSV</Button>}
          {!concluded && canEvaluate && <Button size="sm" onClick={() => void save()} disabled={saving}>{saving ? "Salvando…" : "Salvar"}</Button>}
          {!concluded && canConclude && <Button size="sm" variant="outline" onClick={() => void conclude()}>Concluir</Button>}
        </div>
      </div>

      {concluded && (
        <div className="mt-3 rounded-lg border border-border bg-secondary/40 p-3 text-sm">
          Avaliação concluída — somente leitura.
        </div>
      )}

      <div className="mt-6 flex flex-col gap-6">
        {ficha.stages.map((stage) => {
          const candidateQs = stage.questions.filter((q) => q.visibility !== "EVALUATOR");
          const evaluatorQs = stage.questions.filter((q) => q.visibility === "EVALUATOR");
          const sr = form.stageResults[stage.id] ?? { result: "", note: "" };
          return (
            <section key={stage.id} className="rounded-xl border border-border">
              <header className="flex items-center justify-between border-b border-border bg-secondary/30 px-4 py-2">
                <h2 className="text-sm font-semibold">{stage.orderIndex + 1}. {stage.title}</h2>
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{stage.visibility}</span>
              </header>
              <div className="p-4">
                {candidateQs.length > 0 && (
                  <div className="mb-4">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Respostas do candidato</div>
                    <div className="flex flex-col gap-3">
                      {candidateQs.map((q) => (
                        <div key={q.id} className="rounded-lg bg-secondary/20 p-3">
                          <div className="font-serif text-sm">{q.statement}</div>
                          <div className="mt-1 whitespace-pre-line text-sm text-foreground/80">{candidateAnswerText(q)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="rounded-lg border border-dashed border-border p-3">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Avaliação interna</div>

                  {evaluatorQs.map((q) => (
                    <div key={q.id} className="mb-3">
                      <div className="text-sm font-medium">{q.statement}</div>
                      {q.meta && <ReadonlyGuidance />}
                      {q.helpText && <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">{q.helpText}</p>}
                      <div className="mt-1">
                        {q.answerType === "SCORE" ? (
                          <Input type="number" min={1} max={5} className="w-24" disabled={concluded}
                            value={form.evaluatorAnswers[q.id]?.score ?? ""}
                            onChange={(e) => patch({ evaluatorAnswers: { ...form.evaluatorAnswers, [q.id]: { text: form.evaluatorAnswers[q.id]?.text ?? "", score: e.target.value } } })} />
                        ) : q.answerType === "YES_NO" ? (
                          <div className="flex gap-2">
                            {["Sim", "Não"].map((o) => (
                              <button key={o} type="button" disabled={concluded}
                                onClick={() => patch({ evaluatorAnswers: { ...form.evaluatorAnswers, [q.id]: { text: o, score: form.evaluatorAnswers[q.id]?.score ?? "" } } })}
                                className={`rounded-md border px-3 py-1 text-sm ${form.evaluatorAnswers[q.id]?.text === o ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>{o}</button>
                            ))}
                          </div>
                        ) : (
                          <Textarea rows={3} disabled={concluded}
                            value={form.evaluatorAnswers[q.id]?.text ?? ""}
                            onChange={(e) => patch({ evaluatorAnswers: { ...form.evaluatorAnswers, [q.id]: { text: e.target.value, score: form.evaluatorAnswers[q.id]?.score ?? "" } } })} />
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="mt-2 grid gap-2 sm:grid-cols-[200px_1fr]">
                    <div>
                      <label className="text-xs text-muted-foreground">Resultado da etapa</label>
                      <select className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm" disabled={concluded}
                        value={sr.result}
                        onChange={(e) => patch({ stageResults: { ...form.stageResults, [stage.id]: { ...sr, result: e.target.value } } })}>
                        <option value="">—</option>
                        {STAGE_RESULTS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Observação</label>
                      <Textarea rows={2} className="mt-1" disabled={concluded}
                        value={sr.note}
                        onChange={(e) => patch({ stageResults: { ...form.stageResults, [stage.id]: { ...sr, note: e.target.value } } })} />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* Criteria */}
        <section className="rounded-xl border border-border">
          <header className="border-b border-border bg-secondary/30 px-4 py-2">
            <h2 className="text-sm font-semibold">Critérios (nota 1 a 5)</h2>
          </header>
          <div className="p-4">
            <div className="flex flex-col gap-2">
              {ficha.criteria.map((c) => (
                <div key={c.criterionId} className="flex items-center justify-between gap-3">
                  <span className="text-sm">{c.label} <span className="font-mono text-xs text-muted-foreground">({c.weightPct}%)</span></span>
                  <Input type="number" min={c.scaleMin} max={c.scaleMax} className="w-20" disabled={concluded}
                    value={form.criterionScores[c.criterionId] ?? ""}
                    onChange={(e) => patch({ criterionScores: { ...form.criterionScores, [c.criterionId]: e.target.value } })} />
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              A nota é apenas informativa — nunca aprova ou reprova automaticamente. A decisão é manual.
            </p>
          </div>
        </section>

        {/* Parecer */}
        <section className="rounded-xl border border-border">
          <header className="border-b border-border bg-secondary/30 px-4 py-2">
            <h2 className="text-sm font-semibold">Parecer final</h2>
          </header>
          <div className="grid gap-4 p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="text-xs text-muted-foreground">Teste prático (0 a 5)</label>
                <Input type="number" min={0} max={5} disabled={concluded} value={form.practicalScore}
                  onChange={(e) => patch({ practicalScore: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Potencial (1 a 5)</label>
                <Input type="number" min={1} max={5} disabled={concluded} value={form.potential}
                  onChange={(e) => patch({ potential: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Compatibilidade</label>
                <select className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm" disabled={concluded}
                  value={form.compatibility} onChange={(e) => patch({ compatibility: e.target.value })}>
                  <option value="">—</option>
                  {COMPAT.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            </div>

            {alertOpts.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground">Alertas</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {alertOpts.map((a) => {
                    const on = form.alerts.includes(a);
                    return (
                      <button key={a} type="button" disabled={concluded}
                        onClick={() => patch({ alerts: on ? form.alerts.filter((x) => x !== a) : [...form.alerts, a] })}
                        className={`rounded-md border px-2.5 py-1 text-xs ${on ? "border-destructive bg-destructive/10 text-destructive" : "border-border"}`}>
                        {a}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs text-muted-foreground">Pontos fortes</label>
                <Textarea rows={3} disabled={concluded} value={form.strengths} onChange={(e) => patch({ strengths: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Pontos a desenvolver</label>
                <Textarea rows={3} disabled={concluded} value={form.devPoints} onChange={(e) => patch({ devPoints: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Parecer geral</label>
              <Textarea rows={3} disabled={concluded} value={form.generalOpinion} onChange={(e) => patch({ generalOpinion: e.target.value })} />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs text-muted-foreground">Decisão (manual)</label>
                <select className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm" disabled={concluded}
                  value={form.decision} onChange={(e) => patch({ decision: e.target.value })}>
                  <option value="">—</option>
                  {DECISIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Desempate: confiaria nessa pessoa?</label>
                <select className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm" disabled={concluded}
                  value={form.tiebreakAnswer} onChange={(e) => patch({ tiebreakAnswer: e.target.value })}>
                  <option value="">—</option>
                  {TIEBREAK.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function ReadonlyGuidance() {
  return null; // guidance is rendered from helpText below; kept as a hook point.
}
